<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { deleteAuthUser } from '@core/services/users.js'

const props = defineProps({
  open: { type: Boolean, default: false },
  user: { type: Object, default: null },
})

const emit = defineEmits(['close', 'deleted'])

const deleteConfirmText = ref('')
const deleteLoading = ref(false)
const deleteError = ref(null)
const deleteConfirmInputRef = ref(null)

const isDeleteConfirmValid = computed(() => deleteConfirmText.value === 'DELETE')

// Auto-focus input when modal opens
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      deleteConfirmText.value = ''
      deleteError.value = null
      deleteLoading.value = false
      nextTick(() => {
        if (deleteConfirmInputRef.value) deleteConfirmInputRef.value.focus()
      })
    }
  }
)

function closeModal() {
  emit('close')
}

async function confirmDeleteUser() {
  if (!props.user || !isDeleteConfirmValid.value) return

  deleteLoading.value = true
  deleteError.value = null
  const deletedUid = props.user.uid

  try {
    await deleteAuthUser(deletedUid)
    emit('deleted', deletedUid)
    emit('close')
  } catch (err) {
    console.error('Failed to delete user:', err)
    deleteError.value = err.message || 'שגיאה במחיקת המשתמש'
  } finally {
    deleteLoading.value = false
  }
}
</script>

<template>
  <Transition name="modal">
    <div v-if="open" class="modal-overlay" @click.self="closeModal">
      <div class="modal delete-modal">
        <div class="modal-header modal-header-danger">
          <h2 class="modal-title">מחיקת משתמש</h2>
          <button class="modal-close" @click="closeModal">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="deleteError" class="msg msg-error">{{ deleteError }}</div>

          <div class="delete-info">
            <div class="delete-user-info">
              <span class="delete-user-email">{{ user?.email }}</span>
              <span class="delete-user-uid">{{ user?.uid }}</span>
            </div>

            <div class="delete-warning-box">
              <svg
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <div class="delete-warning-text">
                <p><strong>פעולה זו לא ניתנת לביטול!</strong></p>
                <p>המשתמש יימחק לצמיתות מ-Firebase Auth.</p>
                <p>אם קיים פרופיל ב-Firestore, הוא יסומן כ"נמחק".</p>
              </div>
            </div>

            <div class="delete-confirm-section">
              <label class="delete-confirm-label">הקלד <code>DELETE</code> לאישור:</label>
              <input
                ref="deleteConfirmInputRef"
                v-model="deleteConfirmText"
                type="text"
                class="delete-confirm-input"
                placeholder="DELETE"
                autocomplete="off"
              />
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-cancel" :disabled="deleteLoading" @click="closeModal">ביטול</button>
          <button
            class="btn-delete-confirm"
            :disabled="!isDeleteConfirmValid || deleteLoading"
            @click="confirmDeleteUser"
          >
            <svg
              v-if="deleteLoading"
              class="spin"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
            <span>{{ deleteLoading ? 'מוחק...' : 'מחק לצמיתות' }}</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-lg);
}

.delete-modal {
  width: 100%;
  max-width: 440px;
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
}

.modal-header-danger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: white;
}

.modal-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  margin: 0;
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: var(--spacing-lg);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
}

/* Messages */
.msg {
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  margin-bottom: var(--spacing-md);
}

.msg-error {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error);
  border: 1px solid rgba(239, 68, 68, 0.2);
}

/* Delete info */
.delete-info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.delete-user-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
}

.delete-user-email {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.delete-user-uid {
  font-size: 11px;
  font-family: monospace;
  color: var(--text-muted);
}

.delete-warning-box {
  display: flex;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: var(--border-radius);
  color: var(--color-error);
}

.delete-warning-box svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.delete-warning-text p {
  margin: 0 0 4px 0;
  font-size: var(--font-size-sm);
  line-height: 1.4;
}

.delete-warning-text p:last-child {
  margin-bottom: 0;
}

.delete-confirm-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.delete-confirm-label {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.delete-confirm-label code {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error);
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: var(--font-weight-semibold);
}

.delete-confirm-input {
  padding: var(--spacing-sm) var(--spacing-md);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-base);
  font-family: monospace;
  text-align: center;
  letter-spacing: 2px;
  transition: all var(--transition-fast);
}

.delete-confirm-input:focus {
  outline: none;
  border-color: var(--color-error);
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

/* Buttons */
.btn-cancel {
  padding: var(--spacing-sm) var(--spacing-lg);
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-cancel:hover {
  background: var(--bg-tertiary);
}

.btn-delete-confirm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--color-error);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-delete-confirm:hover:not(:disabled) {
  background: #b91c1c;
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.btn-delete-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Modal Transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .delete-modal,
.modal-leave-active .delete-modal {
  transition: transform 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .delete-modal,
.modal-leave-to .delete-modal {
  transform: scale(0.95);
}
</style>
